<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ChevronRight,
  Clock,
  Loader2,
  PawPrint,
  Plus,
  Search,
  SearchX,
  X,
} from 'lucide-vue-next';
import ClientAvatar from './ClientAvatar.vue';
import IntegrationBadge from './IntegrationBadge.vue';
import {
  animalNames,
  animalsOf,
  clientDisplayName,
  isClientEditable,
} from '@/lib/clients';
import type { ClientSelection, MantaClient, MantaPatient } from '@/types';

const props = defineProps<{
  results: MantaClient[];
  recents: ClientSelection[];
  loading: boolean;
}>();

const query = defineModel<string>('query', { required: true });

const emit = defineEmits<{
  openClient: [client: MantaClient];
  pickAnimal: [client: MantaClient, animal: MantaPatient];
  pickRecent: [recent: ClientSelection];
  create: [];
  skip: [];
}>();

const { t } = useI18n();

const inputEl = ref<HTMLInputElement | null>(null);
const listEl = ref<HTMLElement | null>(null);
const activeIndex = ref(0);

const trimmed = computed(() => query.value.trim());
const hasQuery = computed(() => trimmed.value.length >= 2);
const rowCount = computed(() =>
  hasQuery.value ? props.results.length : props.recents.length,
);
/** The create action sits after the rows so arrow keys reach it too. */
const createIndex = computed(() => rowCount.value);

watch([() => props.results, () => props.recents, hasQuery], () => {
  activeIndex.value = 0;
});

function move(delta: number) {
  const total = rowCount.value + 1;
  activeIndex.value = (activeIndex.value + delta + total) % total;
  void nextTick(() => {
    listEl.value
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex.value}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  });
}

function choose(index: number) {
  if (index === createIndex.value) {
    emit('create');
    return;
  }
  if (hasQuery.value) {
    const client = props.results[index];
    if (client) emit('openClient', client);
  } else {
    const recent = props.recents[index];
    if (recent) emit('pickRecent', recent);
  }
}

function clearQuery() {
  query.value = '';
  inputEl.value?.focus();
}

function phoneOf(client: MantaClient) {
  return client.phone_number?.trim() ?? '';
}
</script>

<template>
  <div class="flex items-center gap-2 border-b px-3">
    <Search class="size-4 shrink-0 text-muted-foreground" />
    <input
      ref="inputEl"
      v-model="query"
      data-autofocus
      class="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      :placeholder="t('workspace.searchCustomerHint')"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="choose(activeIndex)"
    />
    <Loader2
      v-if="loading"
      class="size-4 shrink-0 animate-spin text-muted-foreground"
    />
    <button
      v-else-if="query"
      type="button"
      class="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
      :aria-label="t('common.clear')"
      @click="clearQuery"
    >
      <X class="size-4" />
    </button>
  </div>

  <div ref="listEl" class="max-h-[340px] overflow-y-auto p-1.5">
    <template v-if="hasQuery">
      <div
        v-for="(client, index) in results"
        :key="client.id"
        role="option"
        :data-index="index"
        :aria-selected="activeIndex === index"
        class="group flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2 transition-colors"
        :class="activeIndex === index ? 'bg-accent' : ''"
        @mouseenter="activeIndex = index"
        @click="emit('openClient', client)"
      >
        <ClientAvatar :name="clientDisplayName(client)" />
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5">
            <span class="truncate text-sm font-medium">
              {{ clientDisplayName(client) || t('workspace.unnamedCustomer') }}
            </span>
            <IntegrationBadge
              v-if="!isClientEditable(client)"
              :sub-type="client.integration?.sub_type"
            />
          </div>
          <div
            v-if="phoneOf(client)"
            class="truncate text-xs text-muted-foreground"
          >
            {{ phoneOf(client) }}
          </div>
          <div
            v-if="animalsOf(client).length"
            class="mt-1.5 flex flex-wrap gap-1"
          >
            <button
              v-for="animal in animalsOf(client)"
              :key="animal.id"
              type="button"
              class="inline-flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs transition-colors hover:border-primary hover:text-primary"
              :title="t('workspace.pickWithAnimal', { name: animal.name })"
              @click.stop="emit('pickAnimal', client, animal)"
            >
              <PawPrint class="size-3 shrink-0" />
              <span class="truncate">{{ animal.name || '—' }}</span>
            </button>
          </div>
        </div>
        <ChevronRight
          class="mt-2 size-4 shrink-0 text-muted-foreground transition-opacity"
          :class="activeIndex === index ? 'opacity-100' : 'opacity-0'"
        />
      </div>

      <div
        v-if="!results.length && !loading"
        class="flex flex-col items-center gap-1.5 px-4 py-6 text-center"
      >
        <SearchX class="size-5 text-muted-foreground" />
        <p class="text-sm text-muted-foreground">
          {{ t('workspace.noCustomersFor', { query: trimmed }) }}
        </p>
      </div>
    </template>

    <template v-else-if="recents.length">
      <p
        class="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5 text-xs font-medium text-muted-foreground"
      >
        <Clock class="size-3" />
        {{ t('workspace.recentCustomers') }}
      </p>
      <div
        v-for="(recent, index) in recents"
        :key="recent.clientId"
        role="option"
        :data-index="index"
        :aria-selected="activeIndex === index"
        class="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 transition-colors"
        :class="activeIndex === index ? 'bg-accent' : ''"
        @mouseenter="activeIndex = index"
        @click="emit('pickRecent', recent)"
      >
        <ClientAvatar :name="recent.snapshot.client_name" />
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium">
            {{ recent.snapshot.client_name || t('workspace.unnamedCustomer') }}
          </div>
          <div
            v-if="animalNames(recent.snapshot)"
            class="truncate text-xs text-muted-foreground"
          >
            {{ animalNames(recent.snapshot) }}
          </div>
        </div>
      </div>
    </template>

    <p v-else class="px-4 py-6 text-center text-sm text-muted-foreground">
      {{ t('workspace.searchCustomerEmpty') }}
    </p>
  </div>

  <div class="border-t p-1.5">
    <button
      type="button"
      :data-index="createIndex"
      class="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors"
      :class="activeIndex === createIndex ? 'bg-accent' : ''"
      @mouseenter="activeIndex = createIndex"
      @click="emit('create')"
    >
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-primary/50 text-primary"
      >
        <Plus class="size-4" />
      </span>
      <span class="min-w-0 truncate font-medium">
        {{
          hasQuery
            ? t('sessions.createNamedCustomer', { name: trimmed })
            : t('workspace.createCustomer')
        }}
      </span>
    </button>
  </div>

  <div
    class="flex items-center justify-between border-t bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
  >
    <span class="hidden sm:inline">{{ t('workspace.keyboardHint') }}</span>
    <button
      type="button"
      class="ml-auto rounded px-1.5 py-0.5 hover:bg-accent hover:text-foreground"
      @click="emit('skip')"
    >
      {{ t('workspace.continueWithoutCustomer') }}
    </button>
  </div>
</template>
